// src/services/handoverService.ts
// Fetches and mutates handover packs from the Cloudflare Worker API (Neon database).
// Maps API response to the shape Handover.tsx expects.
// Merge strategy: Neon records take precedence; mock fallback fills gaps.

import { api } from '../utils/api'

// ── API shape (Drizzle returns camelCase from the Worker) ──────────────────
export interface ApiHandoverPack {
  id: string
  jobId: string
  status: string
  asBuiltComplete: boolean
  omManualsComplete: boolean
  itpComplete: boolean
  testRecsComplete: boolean
  notes: string | null
  submittedAt: string | null
  acceptedAt: string | null
  createdAt: string
  updatedAt: string
}

// ── Frontend shape ─────────────────────────────────────────────────────────
export interface MappedHandoverPack {
  id: string
  jobId: string
  status: string                // NOT_STARTED | IN_PROGRESS | SUBMITTED | ACCEPTED
  asBuiltComplete: boolean
  omManualsComplete: boolean
  itpComplete: boolean
  testRecsComplete: boolean
  notes: string | null
  submittedAt: string | null
  acceptedAt: string | null
  createdAt: string
  updatedAt: string
  _fromNeon?: boolean           // merge flag — true for Neon records
}

// ── Mapper ─────────────────────────────────────────────────────────────────
function mapHandoverPack(raw: ApiHandoverPack): MappedHandoverPack {
  return {
    id:                raw.id,
    jobId:             raw.jobId,
    status:            raw.status            ?? 'NOT_STARTED',
    asBuiltComplete:   raw.asBuiltComplete   ?? false,
    omManualsComplete: raw.omManualsComplete ?? false,
    itpComplete:       raw.itpComplete       ?? false,
    testRecsComplete:  raw.testRecsComplete  ?? false,
    notes:             raw.notes             ?? null,
    submittedAt:       raw.submittedAt       ?? null,
    acceptedAt:        raw.acceptedAt        ?? null,
    createdAt:         raw.createdAt,
    updatedAt:         raw.updatedAt,
    _fromNeon:         true,
  }
}

// ── Read ───────────────────────────────────────────────────────────────────
export async function fetchHandoverPacks(jobId?: string): Promise<MappedHandoverPack[]> {
  const qs = jobId ? `?jobId=${encodeURIComponent(jobId)}` : ''
  const { data } = await api.get<{ data: ApiHandoverPack[] } | ApiHandoverPack[]>(
    `/api/handover${qs}`
  )
  const raw: ApiHandoverPack[] = Array.isArray(data) ? data : ((data as any).data ?? [])
  return raw.map(mapHandoverPack)
}

export async function fetchHandoverPack(id: string): Promise<MappedHandoverPack | null> {
  try {
    const { data } = await api.get<{ data: ApiHandoverPack }>(`/api/handover/${id}`)
    return mapHandoverPack((data as any).data)
  } catch {
    return null
  }
}

// ── Create ─────────────────────────────────────────────────────────────────
export async function createHandoverPack(body: {
  jobId: string
  status?: string
  notes?: string | null
}): Promise<MappedHandoverPack> {
  const { data } = await api.post<{ data: ApiHandoverPack }>('/api/handover', body)
  return mapHandoverPack((data as any).data)
}

// ── Update ─────────────────────────────────────────────────────────────────
export async function updateHandoverPack(
  id: string,
  updates: Partial<{
    status: string
    asBuiltComplete: boolean
    omManualsComplete: boolean
    itpComplete: boolean
    testRecsComplete: boolean
    notes: string | null
    submittedAt: string
    acceptedAt: string
  }>
): Promise<MappedHandoverPack> {
  const { data } = await api.patch<{ data: ApiHandoverPack }>(
    `/api/handover/${id}`,
    updates
  )
  return mapHandoverPack((data as any).data)
}
